/**
 * Shared helpers for building the one-liner blurb shown under a twin's name.
 * Used by both TwinPicker (room setup) and ParticipantPopover (room sidebar).
 */

import i18n from '@/i18n'
import { localizedCountryName } from '@/lib/utils/localized-country-name'

function resolveAttr(attrs: Record<string, string>, ...keys: Array<string>): string | null {
  for (const key of keys) {
    const val = attrs[key]
    if (val && val.trim() && val.trim().toLowerCase() !== 'unknown') return val.trim()
  }
  return null
}

/**
 * Human-friendly gender label for twin picker blurbs (Man / Woman, not Male / Female).
 * Keep in sync with PersonaListProjectionService.toBlurbGenderLabel on the backend.
 *
 * Gender is a closed set. A value outside it (a rating answer, a coded miss)
 * is omitted rather than shown in the gender slot.
 */
export function formatGenderForBlurb(gender: string, locale: string = i18n.language): string {
  const trimmed = gender.trim()
  if (!trimmed) return ''

  const g = trimmed.toLowerCase()

  if (
    g === 'woman' ||
    g === 'w' ||
    g === 'female' ||
    g === 'f' ||
    g === 'femenino' ||
    g === 'feminine' ||
    g === 'mujer' ||
    g.includes('female')
  ) {
    return String(i18n.t('blurbWoman', { ns: 'common', lng: locale }))
  }

  if (
    g === 'man' ||
    g === 'm' ||
    g === 'male' ||
    g === 'masculino' ||
    g === 'masculine' ||
    g === 'hombre' ||
    (g.includes('male') && !g.includes('female'))
  ) {
    return String(i18n.t('blurbMan', { ns: 'common', lng: locale }))
  }

  if (
    g === 'non-binary' ||
    g === 'nonbinary' ||
    g === 'non binary' ||
    g === 'non_binary' ||
    g === 'other'
  ) {
    return trimmed
  }

  return ''
}

/** ISO 3166-1 alpha-2 or English country name → display name in the UI language. */
export function formatCountryForDisplay(country: string, locale: string = i18n.language): string {
  return localizedCountryName(country, locale)
}

export function buildLocationLine(
  attrs: Record<string, string>,
  locale: string = i18n.language,
): string | null {
  const city = resolveAttr(attrs, 'std_geo_city', 'City', 'city')
  const countryRaw = resolveAttr(attrs, 'std_geo_country', 'Country', 'country', '_resolved_country')
  const country = countryRaw ? formatCountryForDisplay(countryRaw, locale) : null
  if (city && country) return `${city}, ${country}`
  return country ?? city ?? null
}

/**
 * Age · gender · country line for twin picker cards when no enrichment bio is available.
 */
export function buildPickerDemographicsLine(
  attrs: Record<string, string>,
  locale: string = i18n.language,
): string | null {
  const parts: Array<string> = []
  const age = resolveAttr(attrs, 'std_dem_age', 'Age', 'What is your age?', 'age_range', 'age', '_resolved_age')
  if (age) parts.push(age)
  const gender = resolveAttr(attrs, 'std_dem_gender', 'Gender', '_resolved_gender', 'gender', 'sex')
  const genderLabel = gender ? formatGenderForBlurb(gender, locale) : ''
  if (genderLabel) parts.push(genderLabel)
  const countryRaw = resolveAttr(attrs, 'std_geo_country', 'Country', 'country', '_resolved_country')
  if (countryRaw) parts.push(formatCountryForDisplay(countryRaw, locale))
  return parts.length > 0 ? parts.join(' · ') : null
}

export function buildBlurbLine(
  attrs: Record<string, string>,
  audienceType: string | null,
  locale: string = i18n.language,
): string | null {
  const isB2B = audienceType?.toLowerCase() === 'b2b'
  const parts: Array<string> = []

  if (isB2B) {
    const title = resolveAttr(attrs, 'std_prof_title', 'Job Title', 'std_prof_function', 'Job Function')
    if (title) parts.push(title)
    const industry = resolveAttr(attrs, 'std_firm_industry', 'Industry')
    if (industry) parts.push(industry)
    const size = resolveAttr(attrs, 'std_firm_size', 'Company Size')
    if (size) parts.push(size)
  } else {
    const age = resolveAttr(attrs, 'std_dem_age', 'Age', 'What is your age?', 'age_range', 'age', '_resolved_age')
    if (age) parts.push(age)
    const gender = resolveAttr(attrs, 'std_dem_gender', 'Gender', '_resolved_gender', 'gender', 'sex')
    const genderLabel = gender ? formatGenderForBlurb(gender, locale) : ''
    if (genderLabel) {
      parts.push(genderLabel)
    }
  }

  return parts.length > 0 ? parts.join(' · ') : null
}

/**
 * Auto-detect audience type from demographics.
 * If B2B-specific canonical fields exist, treat as B2B; otherwise B2C.
 */
export function inferAudienceType(attrs: Record<string, string>): 'b2b' | 'b2c' {
  const b2bKeys = ['std_prof_title', 'Job Title', 'std_prof_function', 'Job Function', 'std_firm_industry', 'Industry']
  for (const key of b2bKeys) {
    if (attrs[key] && attrs[key].trim()) return 'b2b'
  }
  return 'b2c'
}

/**
 * Safely extract conversation starters from a FeaturedTwin's profileCard.
 * Handles both the typed ProfileCard shape and the looser Record<string, unknown> path.
 */
export function getProfileCardStarters(twin: { profileCard?: { bio?: string; conversation_starters?: Array<string> } | Record<string, unknown> | null } | null): Array<string> {
  if (!twin?.profileCard) return []
  const card = twin.profileCard as Record<string, unknown>
  const starters = card.conversation_starters
  if (!Array.isArray(starters)) return []
  return starters.filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
}
